import ResponsiveImage from "../../common/ResponsiveImage";
import { tr, usePartnerTranslation } from "../../../lib/language";
import React, { useRef, useState } from "react";
import { ImagePlus, Trash2, Star, Plus, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { FieldRow, SectionHeader, CheckboxField } from "./fields";
import ShopImageCropper from "../ShopImageCropper";
import {
  PHOTO_ACCEPTED,
  PHOTO_MAX_BYTES,
  PHOTO_MAX_COUNT,
  PHOTO_MIN_COUNT,
  PHOTO_MIN_WIDTH,
  PHOTO_MIN_HEIGHT,
} from "./wizardConstants";
import { cn } from "../../../lib/utils";

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const loadDimensions = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = dataUrl;
  });

async function validateAndReadFile(file) {
  if (!PHOTO_ACCEPTED.includes(file.type)) {
    toast.error(tr("Unsupported format"), {
      description: tr("Please pick a JPEG, PNG, or WebP image."),
    });
    return null;
  }
  if (file.size > PHOTO_MAX_BYTES) {
    toast.error(tr("{{value0}} is too large", { value0: file.name || "Photo" }), {
      description: tr("Please pick a file under {{value0}} MB.", { value0: 20 }),
    });
    return null;
  }
  let dataUrl;
  try {
    dataUrl = await readFileAsDataUrl(file);
  } catch {
    toast.error(tr("Couldn't read the file"));
    return null;
  }
  let dims;
  try {
    dims = await loadDimensions(dataUrl);
  } catch {
    toast.error(tr("Couldn't read the image"));
    return null;
  }
  if (dims.width < PHOTO_MIN_WIDTH || dims.height < PHOTO_MIN_HEIGHT) {
    toast.error(tr("Photo is too small"), {
      description: tr("Min {{value0}}×{{value1}} px. You picked {{value2}}×{{value3}} px.", { value0: PHOTO_MIN_WIDTH, value1: PHOTO_MIN_HEIGHT, value2: dims.width, value3: dims.height }),
    });
    return null;
  }
  return { url: dataUrl, name: file.name || "photo", width: dims.width, height: dims.height };
}

const PhotoTile = ({
  index,
  photo,
  isMain,
  onRemove,
  onMakeMain,
  onDragStart,
  onDragOver,
  onDrop
}) => {
  usePartnerTranslation();
  return <div draggable onDragStart={e => onDragStart(e, index)} onDragOver={e => onDragOver(e, index)} onDrop={e => onDrop(e, index)} data-testid={`photo-${index}`} className={cn("group relative aspect-[16/9] overflow-hidden rounded-xl border-2 bg-[#F3EEEE] transition-all", isMain ? "border-[#FF5757] ring-2 ring-[#FFEFEF]" : "border-[#EAECEF]")}>
    <ResponsiveImage imageWidth={960} src={photo.url} alt={photo.name || tr("Photo {{value0}}", {
      value0: index + 1
    })} className="h-full w-full object-cover" data-testid={`photo-${index}-img`} />

    {isMain && <span data-testid={`photo-${index}-main-badge`} className="absolute start-2 top-2 inline-flex items-center gap-1 rounded-full bg-[#FF5757] px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm">
        <Star className="h-3 w-3" fill="white" strokeWidth={0} />{tr("Main")}</span>}

    <span aria-hidden className="pointer-events-none absolute end-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-md bg-white/90 text-[#6B7280] opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
      <GripVertical className="h-3.5 w-3.5" />
    </span>

    <div className="absolute inset-x-0 bottom-0 flex items-center justify-end gap-1 bg-gradient-to-t from-black/55 to-transparent px-2 py-2 opacity-0 transition-opacity group-hover:opacity-100">
      {!isMain && <button type="button" onClick={() => onMakeMain(index)} data-testid={`photo-${index}-make-main`} className="inline-flex h-7 items-center gap-1 rounded-md bg-white/95 px-2 text-[11px] font-semibold text-[#0F1115] hover:bg-white">
          <Star className="h-3 w-3" />{tr("Make main")}</button>}
      <button type="button" onClick={() => onRemove(index)} data-testid={`photo-${index}-remove`} aria-label={tr("Remove photo")} className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-white/95 text-[#9B0000] hover:bg-white">
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  </div>;
};

const Step3Visuals = ({ data, update, errors = {} }) => {
  usePartnerTranslation();
  const inputRef = useRef(null);
  const dragFromRef = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [cropQueue, setCropQueue] = useState([]);
  const [cropIndex, setCropIndex] = useState(0);
  const [croppedPhotos, setCroppedPhotos] = useState([]);
  const photos = data.photos || [];
  const croppingPhoto = cropQueue[cropIndex] || null;

  const acceptString = PHOTO_ACCEPTED.join(",");

  const handleFiles = async (files) => {
    const remaining = PHOTO_MAX_COUNT - photos.length;
    if (remaining <= 0) {
      toast.error(tr("Photo limit reached"), {
        description: tr("Max {{value0}} photos.", { value0: PHOTO_MAX_COUNT }),
      });
      return;
    }
    const list = Array.from(files).slice(0, remaining);
    if (list.length < files.length) {
      toast.message(tr("Some photos skipped"), {
        description: `Only the first ${remaining} were added (${PHOTO_MAX_COUNT} max).`,
      });
    }
    const validated = [];
    for (const file of list) {
      const ok = await validateAndReadFile(file);
      if (ok) validated.push({ url: ok.url, name: ok.name });
    }
    if (validated.length === 0) return;
    setCropQueue(validated);
    setCropIndex(0);
    setCroppedPhotos([]);
  };

  const resetCropQueue = () => {
    setCropQueue([]);
    setCropIndex(0);
    setCroppedPhotos([]);
  };

  const handleCropApply = (dataUrl) => {
    if (!croppingPhoto) return;
    const nextCropped = [
      ...croppedPhotos,
      { url: dataUrl, name: croppingPhoto.name },
    ];

    if (cropIndex < cropQueue.length - 1) {
      setCroppedPhotos(nextCropped);
      setCropIndex((idx) => idx + 1);
      return;
    }

    update("photos", [...photos, ...nextCropped]);
    toast.success(tr("Photos added: {{count}}", { count: nextCropped.length }));
    resetCropQueue();
  };

  const onPickClick = () => inputRef.current?.click();

  const onInputChange = async (e) => {
    if (e.target.files?.length) await handleFiles(e.target.files);
    e.target.value = ""; // allow same file re-pick
  };

  const remove = (i) => {
    const next = photos.filter((_, idx) => idx !== i);
    update("photos", next);
  };

  const makeMain = (i) => {
    if (i === 0) return;
    const next = [...photos];
    const [picked] = next.splice(i, 1);
    next.unshift(picked);
    update("photos", next);
    toast.success(tr("Main photo updated"));
  };

  /* ---- Drag & drop reordering ---- */
  const onDragStart = (e, idx) => {
    dragFromRef.current = idx;
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", String(idx));
    } catch {
      /* ignore */
    }
  };
  const onDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIdx(idx);
  };
  const onDrop = (e, idx) => {
    e.preventDefault();
    setDragOverIdx(null);
    const from =
      dragFromRef.current ?? Number(e.dataTransfer.getData("text/plain"));
    if (Number.isNaN(from) || from === idx) return;
    const next = [...photos];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    update("photos", next);
    dragFromRef.current = null;
  };

  const tilesError = errors.photos;

  return (
    <section data-testid="step-visuals" className="space-y-6">
      <SectionHeader
        title={tr("Visuals")}
        subtitle={tr("Photos are what customers see first — they decide in seconds.")}
      />

      <div
        data-testid="photo-content-guidelines"
        className="rounded-lg border border-[#F0F2F5] bg-[#FAFAFB] p-4"
      >
        <p className="text-sm font-semibold text-[#0F1115]">{tr("Photo Content Guidelines")}</p>
        <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-[#6B7280]">
          <li>{tr("· Use candid shots of real participants — authentic moments outperform posed or filtered images every time")}</li>
          <li>{tr("· Show exactly what guests can expect; accurate photos set the right expectations and convert better")}</li>
          <li>{tr("· Keep images clean — no watermarks, overlays, stock photos, or promotional text")}</li>
        </ul>
      </div>

      <p
        data-testid="photo-rules"
        className="text-xs text-[#6B7280]"
      >
        <strong className="font-semibold text-[#0F1115]">{tr("Photo requirements:")}</strong>{" "}
        {tr("JPEG / PNG / WebP · at least")}{" "}
        <strong className="font-semibold text-[#0F1115]">
          {PHOTO_MIN_WIDTH}×{PHOTO_MIN_HEIGHT}{tr(" px")}</strong>{" "}{tr("· under")}{" "}
        <strong className="font-semibold text-[#0F1115]">{"20 MB"}</strong> ·{" "}
        <strong className="font-semibold text-[#0F1115]">
          {PHOTO_MIN_COUNT}–{PHOTO_MAX_COUNT}
        </strong>{" "}{tr("photos. You'll crop each upload into the listing format before it is added.")}</p>

      <FieldRow
        label={tr("Photos")}
        required
        testid="field-photos"
        helper={tr("{{value0}} of {{value1}} added (need at least {{value2}}).", { value0: photos.length, value1: PHOTO_MAX_COUNT, value2: PHOTO_MIN_COUNT })}
        error={tilesError}
        info={tr("The first photo is the main photo. Make it the strongest, well-composed shot of your experience — that's what wins clicks.")}
      >
        <input
          ref={inputRef}
          type="file"
          accept={acceptString}
          multiple
          className="hidden"
          onChange={onInputChange}
          data-testid="photos-file-input"
        />

        {photos.length === 0 ? (
          <button
            type="button"
            onClick={onPickClick}
            data-testid="photos-pick-empty"
            className="flex aspect-[16/9] w-full max-w-[640px] flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#CFD2D8] bg-[#FAFAFB] text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115] hover:bg-[#F3EEEE]"
          >
            <ImagePlus className="h-8 w-8 text-[#FF5757]" strokeWidth={1.6} />{tr("Upload and crop photos")}<span className="text-xs font-normal text-[#6B7280]">{tr("You can pick multiple at once.")}</span>
          </button>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {photos.map((p, i) => (
                <PhotoTile
                  key={i}
                  index={i}
                  photo={p}
                  isMain={i === 0}
                  onRemove={remove}
                  onMakeMain={makeMain}
                  onDragStart={onDragStart}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                />
              ))}
              {photos.length < PHOTO_MAX_COUNT && (
                <button
                  type="button"
                  onClick={onPickClick}
                  data-testid="photos-pick-more"
                  className="flex aspect-[16/9] flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-[#CFD2D8] bg-[#FAFAFB] text-xs font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115] hover:bg-[#F3EEEE]"
                >
                  <Plus className="h-5 w-5 text-[#FF5757]" strokeWidth={1.8} />{tr("Add and crop more photos")}</button>
              )}
            </div>
          </>
        )}
      </FieldRow>

      <div
        data-testid="copyright-confirm-block"
        data-error-anchor={errors.copyright_confirmed ? "true" : undefined}
        className={cn(
          "rounded-lg border bg-white p-3.5 transition-colors",
          errors.copyright_confirmed
            ? "border-[#9B0000] bg-[#FFF5F5]"
            : "border-[#F0F2F5]",
        )}
      >
        <CheckboxField
          value={data.copyright_confirmed}
          onChange={(v) => update("copyright_confirmed", v)}
          testid="copyright-confirm"
          label={
            <span className="block text-sm leading-relaxed text-[#0F1115]">{tr("I confirm that I own the copyright for these pictures and have obtained model release forms for any recognizable faces depicted. I affirm that I have not used any trademarks, logos, or imagery from third parties without proper authorization. I understand that I am liable for any copyright or trademark infringement.")}</span>
          }
        />
        {errors.copyright_confirmed && (
          <p className="mt-2 text-xs font-medium text-[#9B0000]">
            {errors.copyright_confirmed}
          </p>
        )}
      </div>

      <ShopImageCropper
        open={!!croppingPhoto}
        imageSrc={croppingPhoto?.url}
        aspect={16 / 9}
        cropShape="rect"
        title={tr("Crop photo {{value0}} of {{value1}}", { value0: Math.min(cropIndex + 1, cropQueue.length), value1: cropQueue.length })}
        helperText="Adjust any portrait, square, or landscape photo into the 16:9 listing format."
        preserveResolution
        outputSize={{ width: 1600, height: 900 }}
        applyLabel={cropIndex < cropQueue.length - 1 ? "Apply and next" : "Apply"}
        onClose={resetCropQueue}
        onApply={handleCropApply}
      />
    </section>
  );
};

export default Step3Visuals;
